<template>
  <main-large-dialog
    v-bind="$props"
    :is-visible.sync="computedIsVisible"
    :is-valid="computedIsValid"
    :title="$i18n.t('dialog.attachments.addAttachment')"
    agree-text="dialog.title.add"
    dialog-width="60%"
    min-body-height="300px"
    max-height="60vh"
    no-paddings-y
    no-paddings-x
    @agree="handleAttachments"
    @modalClosed="onClose"
    :loading="loading"
  >
    <template #body>
      <v-tabs v-model="currentTab" class="fill-height d-flex flex-column" hide-slider color="font">
        <v-tab
          v-for="tab in computedTabs"
          :key="tab.value"
          v-text="tab.text"
          active-class="activeTab"
          class="font--text"
          style="text-transform: none;"
        ></v-tab>
        <v-tabs-items v-model="currentTab" class="fill-height">
          <v-tab-item style="height: inherit">
            <v-form v-model="isValid">
              <add-attachment-tab
                ref="addAttachment"
                :features="features"
                @fileUploaded="fileUploaded"
                @fileError="loading = false"
                @fileAdded="fileAdded"
                @fileRemoved="fileRemoved"
                :expanded="expanded"
                :formSchema="formSchema"
              />
            </v-form>
          </v-tab-item>
          <v-tab-item style="height: inherit">
            <assign-attachment-tab
              :attachments="filteredAttachments(attachments)"
              @selectAttachments="setSelected"
              :selected="selectedAttachments"
              subheader="dialog.attachments.assignFiles"
              :single-select="isSingleSelect"
            />
          </v-tab-item>
          <v-tab-item style="height: inherit">
            <assign-attachment-tab
              :attachments="filteredAttachments(externalAttachments)"
              @selectAttachments="setSelected"
              :selected="selectedExternalAttachments"
              subheader="dialog.attachments.assignFtpFiles"
              is-external-attachments
              :single-select="isSingleSelect"
            />
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
    </template>
  </main-large-dialog>
</template>

<script>
import AssignAttachmentTab from '@/components/AssignAttachmentTab';
import AddAttachmentTab from '@/components/AddAttachmentTab';
import { call, get } from 'vuex-pathify';
import MainLargeDialog from '@/components/MainLargeDialog';

import defaultValues from '@/mixins/defaultValues';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'AttachmentsDialog',
  components: { AddAttachmentTab, AssignAttachmentTab, MainLargeDialog },
  mixins: [defaultValues, prepareAttributesFeatureCardMixin],
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    features: {
      type: Array,
      required: true,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    formSchema: {
      type: Array,
      default: () => {
        return [];
      },
    },

    customDataSourceName: {
      type: String,
    },
    customFeatureId: {
      type: [Number, String],
    },
  },
  data: () => ({
    currentTab: 0,
    isValid: false,
    hasFile: false,
    selectedAttachments: [],
    selectedExternalAttachments: [],
    loading: false,
    isNew: true, // need for 'getValue' in prepareAttributesFeatureCardMixin
  }),
  computed: {
    attachmentsForFeature: get('relations/attachmentsForFeature@:dataSourceName@:featureId'),
    computedIsValid() {
      const tabsValidators = [
        this.isValid && this.hasFile,
        this.selectedAttachments?.length > 0,
        this.selectedExternalAttachments?.length > 0,
      ];
      return tabsValidators[this.currentTab];
    },
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    attachments: get('relations/attachments'),

    currentLayerDataSourceName: get('layers/currentLayer@data_source_name'),
    currentLayerFeatureId() {
      return this.$route.params.fid;
    },
    dataSourceName() {
      return this.customDataSourceName || this.currentLayerDataSourceName;
    },
    featureId() {
      if (this.customFeatureId || this.customFeatureId === 0) {
        return this.customFeatureId;
      }
      return this.currentLayerFeatureId;
    },

    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    externalAttachments: get('relations/externalAttachments'),
    isExternalAttachmentsModuleEnabled: get('admin/settingsValues@external_attachments_module_enabled'),
    isExternalAttachmentsSuperAdminEnabled: get('admin/additionalModules@EXTERNAL_ATTACHMENTS.enabled'),
    isExternalAttachmentsModuleConfigured: get('admin/additionalModules@EXTERNAL_ATTACHMENTS.configured'),
    isExternalAttachmentsVisible() {
      return (
        this.isExternalAttachmentsModuleEnabled &&
        this.isExternalAttachmentsSuperAdminEnabled &&
        this.isExternalAttachmentsModuleConfigured
      );
    },
    isSingleSelect() {
      return !(this.features?.length || false);
    },
    computedTabs() {
      return [
        { text: this.$i18n.t('dialog.title.add'), value: 'add' },
        { text: this.$i18n.t('dialog.title.assign'), value: 'assign' },
        ...(this.isExternalAttachmentsVisible
          ? [
              {
                text: this.$i18n.t('dialog.title.downloadFromFtp'),
                value: 'downloadFromFtp',
              },
            ]
          : []),
      ];
    },
  },
  methods: {
    getAttachments: call('relations/getAttachments'),
    getRelations: call('relations/getRelations'),
    assignAttachments: call('relations/assignAttachments'),
    getExternalAttachments: call('relations/getExternalAttachments'),
    assignExternalAttachments: call('relations/assignExternalAttachments'),
    filteredAttachments(attachments) {
      const attachmentsForFeatureIds = this.attachmentsForFeature?.map(attachment => attachment.feature_id);
      return attachments?.filter(attachment => !attachmentsForFeatureIds?.includes(attachment.id)) || [];
    },
    async handleAdd() {
      this.loading = true;
      if (this.$refs.addAttachment.$refs.file.$refs.dropzone.getAcceptedFiles().length < 1) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.filesRequired'),
        });
        this.loading = false;
      } else {
        this.$refs.addAttachment.uploadFiles();
      }
    },
    async fileUploaded(attachmentsIds) {
      if (this.features?.length) {
        if (this.currentLayerDataSourceName && (this.featureId || this.featureId === 0)) {
          const payload = {
            data_source_name: this.currentLayerDataSourceName,
            feature_id: this.featureId,
          };
          await Promise.all([this.getRelations(payload), await this.getAttachments()]);
        }
        this.$root.$emit('refreshTableData');
        this.$root.$emit('refreshFormData');
        const layersIds = this.projectLayers
          .filter(layer => layer.data_source_name === this.currentLayerDataSourceName)
          .map(layer => layer.id);
        if (layersIds?.length) this.$root.$emit('updateLegendStylesCounts', { layersIds });
      }
      this.$emit(
        'filesUploaded',
        attachmentsIds.map(attachmentId => {
          return { id: attachmentId };
        })
      );
      this.computedIsVisible = false;
      this.loading = false;
    },
    setSelected(data) {
      if (this.currentTab === 1) {
        this.selectedAttachments = data;
      } else {
        this.selectedExternalAttachments = data;
      }
    },
    handleAttachments() {
      return this.currentTab === 0
        ? this.handleAdd()
        : this.currentTab === 1
        ? this.handleAssign(this.selectedAttachments, 'attachment_id', this.assignAttachments)
        : this.handleAssign(this.selectedExternalAttachments, 'id', this.assignExternalAttachments);
    },
    async handleAssign(selectedAttachments, idKey, fn) {
      try {
        this.loading = true;
        if (this.features?.length) {
          const payload = selectedAttachments.map(r => {
            return {
              [idKey]: r.id,
              objects: this.features,
            };
          });
          await fn(payload);
          if (this.currentLayerDataSourceName && (this.featureId || this.featureId === 0)) {
            const payload = {
              data_source_name: this.currentLayerDataSourceName,
              feature_id: this.featureId,
            };
            await this.getRelations(payload);
          }
          this.$root.$emit('refreshTableData');
          this.$root.$emit('refreshFormData');
          const layersIds = this.projectLayers
            .filter(layer => layer.data_source_name === this.currentLayerDataSourceName)
            .map(layer => layer.id);
          if (layersIds?.length) this.$root.$emit('updateLegendStylesCounts', { layersIds });
          this.$root.$emit('fileUploaded');
        }
        this.$emit('filesAssigned', this.selectedAttachments);
        this.computedIsVisible = false;
        this.setSelected([]);
      } finally {
        this.loading = false;
      }
    },
    onClose() {
      this.setSelected([]);
      this.$refs.addAttachment?.$refs.file?.$refs.dropzone.removeAllFiles();
    },
    fileAdded() {
      this.hasFile = true;
    },
    fileRemoved() {
      this.hasFile = false;
    },
    async init() {
      await Promise.all([
        this.getAttachments(),
        ...[this.isExternalAttachmentsVisible && this.getExternalAttachments()],
      ]);
    },
  },
  mounted() {
    this.init();
    this.$watch(
      vm => {
        return { currentTab: vm.currentTab, expanded: vm.expanded };
      },
      nV => {
        if (nV.currentTab === 0 && nV.expanded) {
          this.$refs.addAttachment.values = this.getValues(this.formSchema, {});
        }
      },
      {
        deep: true,
        immediate: true,
      }
    );
  },
};
</script>

<style lang="scss" scoped>
.activeTab {
  background-color: rgba(207, 207, 207, 0.25);
}
::v-deep {
  .v-input--selection-controls__input .v-icon {
    font-size: 19px;
  }
  .v-slide-group {
    z-index: 99;
    position: sticky;
    top: 0;
    box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12) !important;
  }
}
</style>
